import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { ArrowClockwise, Broadcast, Clock, Warning, TrendUp, CloudRain, Wind, Thermometer, Sparkle, FirstAid, Target, Cpu } from "@phosphor-icons/react";
import { api } from "@/lib/api";
import { TeamTag } from "@/components/TeamVisuals";
import { TID } from "@/constants/testIds";
import SnapSurge from "@/components/SnapSurge";
import GameCardGrid from "@/components/GameCardGrid";
import PivotBoard from "@/components/PivotBoard";
import StrategyModeBanner from "@/components/StrategyModeBanner";
import AIAnalysis from "@/pages/AIAnalysis";
import OwnershipCalibrationPanel from "@/components/OwnershipCalibrationPanel";
import { humanizeJobId, relativeTime } from "@/constants/jobLabels";

const ACTIVE_PRESET_KEY = "wp_active_preset";
const CUSTOM_PARAMS_KEY = "wp_custom_preset_params";

const DEFAULT_CUSTOM_PARAMS = {
  field_size: 100000,
  num_lineups: 20,
  max_exposure_pct: 40,
  sum_own_min: 90,
  sum_own_max: 140,
  low_owned_thresh: 6,
  min_low_owned: 1,
  global_variance: 0.06,
};

function loadCustomParams() {
  try {
    const raw = localStorage.getItem(CUSTOM_PARAMS_KEY);
    if (!raw) return { ...DEFAULT_CUSTOM_PARAMS };
    const parsed = JSON.parse(raw);
    return { ...DEFAULT_CUSTOM_PARAMS, ...parsed };
  } catch { return { ...DEFAULT_CUSTOM_PARAMS }; }
}

function saveCustomParams(params) {
  try { localStorage.setItem(CUSTOM_PARAMS_KEY, JSON.stringify(params)); }
  catch { /* private mode safe */ }
}

// JOB_LABELS + humanizeJobId + relativeTime moved to
// /app/frontend/src/constants/jobLabels.js so future consumers (KickoffCountdown,
// health dashboard) can share the same labels without importing a page component.

export default function DataPipeline() {
  const [snap, setSnap] = useState(null);
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [weather, setWeather] = useState(null);
  const [weatherLoading, setWeatherLoading] = useState(false);
  const [attackPlan, setAttackPlan] = useState(null);
  const [attackPlanLoading, setAttackPlanLoading] = useState(false);
  const [injuries, setInjuries] = useState(null);
  const [injuriesLoading, setInjuriesLoading] = useState(false);
  const [presets, setPresets] = useState({});
  const [activePreset, setActivePreset] = useState(
    (typeof localStorage !== "undefined" && localStorage.getItem(ACTIVE_PRESET_KEY)) || "milly_maker"
  );
  // Custom preset params — only used when activePreset === 'custom'. Persisted
  // to localStorage so users don't lose their manual tuning across refreshes.
  const [customParams, setCustomParams] = useState(() => loadCustomParams());
  // Slate awareness: once a slate is loaded (via CSV upload OR /api/dk/slates
  // apply), the Attack Plan + Deep Focus panels should stop saying "Load
  // DKSalaries" and start saying "your slate is loaded — hit Run". Powered
  // by /api/slate/info (n_players > 0 == slate loaded). Refreshes on the
  // `wp:pool-refreshed` event fired by Uploads.jsx after a slate apply.
  const [slateInfo, setSlateInfo] = useState(null);
  // Sprint 60.14 — slate-scoped tables. We fetch /api/slate/games (same
  // source the ticker / game card grid / pivot board use) and pull out
  // the set of team abbreviations in the currently-applied slate. The
  // Vegas Slate table and Defense-vs-Position Ranks below then filter
  // to just those teams — no more "Vegas Slate (278 games)" showing the
  // whole league when the user only cares about 12 SUN Main games.
  const [slateTeams, setSlateTeams] = useState(null); // null = not loaded, Set = loaded
  const [slateGameIds, setSlateGameIds] = useState(null); // Set of AWAY@HOME
  const refreshSlateTeams = async () => {
    try {
      const r = await api.slateGames();
      const teams = new Set();
      const gids = new Set();
      (r?.games || []).forEach((g) => {
        if (g.home) teams.add(g.home);
        if (g.away) teams.add(g.away);
        if (g.away && g.home) gids.add(`${g.away}@${g.home}`);
      });
      setSlateTeams(teams);
      setSlateGameIds(gids);
    } catch (e) {
      console.debug("slateGames load failed:", e?.message || e);
    }
  };
  const refreshSlateInfo = async () => {
    try {
      const info = await api.slateInfo();
      setSlateInfo(info);
    } catch (e) {
      console.debug("slateInfo load failed:", e?.message || e);
    }
  };

  const load = async () => {
    try {
      const [s, j] = await Promise.all([api.dataSnapshot(), api.dataScheduler()]);
      setSnap(s);
      setJobs(j.jobs || []);
    } catch (e) {
      console.debug("data snapshot/scheduler load failed:", e?.message || e);
    }
  };

  const loadWeather = async () => {
    setWeatherLoading(true);
    try {
      const w = await api.slateWeather();
      setWeather(w);
    } catch (e) {
      console.debug("slate weather load failed (pool may not be uploaded):", e?.message || e);
    } finally {
      setWeatherLoading(false);
    }
  };

  useEffect(() => {
    load(); loadWeather(); loadInjuries(); refreshSlateInfo(); refreshSlateTeams();
    api.contestPresets().then((d) => setPresets(d.presets || {}))
      .catch((e) => console.debug("[DataPipeline] contestPresets fetch failed:", e?.message || e));
    // Auto-loaded plan on mount (persisted in state.last_attack_plan by backend)
    api.aiAttackPlanLast?.().then((d) => { if (d?.plan) setAttackPlan(d); })
      .catch((e) => console.debug("[DataPipeline] aiAttackPlanLast fetch failed:", e?.message || e));
    // React to preset changes made in Optimizer/WarRoom or Kickoff Countdown
    const onStorage = (e) => {
      if (e.key === ACTIVE_PRESET_KEY && e.newValue) setActivePreset(e.newValue);
    };
    const onPresetChange = (e) => {
      const next = e?.detail?.preset;
      if (next) setActivePreset(next);
    };
    // Auto-generated plans from KickoffCountdown scheduler
    const onAutoPlan = (e) => {
      const plan = e?.detail?.plan;
      if (plan) {
        setAttackPlan(plan);
        toast.success(`Auto Attack Plan drafted — ${plan.preset_label || plan.preset || "slate"}`);
      }
    };
    // Slate applied via Uploads chip / CSV upload → refresh awareness
    const onPoolRefreshed = () => { refreshSlateInfo(); refreshSlateTeams(); };
    window.addEventListener("storage", onStorage);
    window.addEventListener("wp:preset-change", onPresetChange);
    window.addEventListener("wp:attack-plan-auto", onAutoPlan);
    window.addEventListener("wp:pool-refreshed", onPoolRefreshed);
    return () => {
      window.removeEventListener("storage", onStorage);
      window.removeEventListener("wp:preset-change", onPresetChange);
      window.removeEventListener("wp:attack-plan-auto", onAutoPlan);
      window.removeEventListener("wp:pool-refreshed", onPoolRefreshed);
    };
  }, []);

  const loadInjuries = async () => {
    setInjuriesLoading(true);
    try {
      const d = await api.injuriesSlate();
      setInjuries(d);
    } catch (e) { console.debug("slate injuries load failed:", e?.message || e); }
    finally { setInjuriesLoading(false); }
  };

  const generateAttackPlan = async () => {
    setAttackPlanLoading(true);
    try {
      const d = await api.aiAttackPlan(
        activePreset,
        activePreset === "custom" ? customParams : undefined,
      );
      setAttackPlan(d);
      const modeSuffix = d.strategy_mode ? ` · ${d.strategy_mode.toUpperCase()}-FIELD MODE` : "";
      toast.success(`Attack Plan generated — ${d.slate_type} slate, ${d.n_games} games${modeSuffix}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Attack plan failed");
    } finally {
      setAttackPlanLoading(false);
    }
  };

  const updateCustomParam = (key, value) => {
    setCustomParams((prev) => {
      const next = { ...prev, [key]: value };
      saveCustomParams(next);
      // Broadcast so the Optimizer (and anything else listening) can merge
      // the custom knobs into its `cfg` on the fly.
      try {
        window.dispatchEvent(new CustomEvent("wp:custom-preset-change", {
          detail: { params: next, source: "data-pipeline" },
        }));
      } catch { /* CustomEvent unsupported */ }
      return next;
    });
  };

  const refresh = async () => {
    setLoading(true);
    try {
      const r = await api.dataRefresh();
      toast.success(`Refreshed: ${Object.entries(r.counts).map(([k, v]) => `${k}=${v}`).join(", ")}`);
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Refresh failed");
    } finally {
      setLoading(false);
    }
  };

  const vegas = snap?.vegas || [];
  const defRanks = snap?.def_ranks || {};
  const qbr = snap?.qbr || {};

  // Memoize expensive sorts so they don't run on every render. Scope
  // both tables to the currently-applied slate when we know it: users
  // don't care about all 278 Vegas games or all 32 defense ranks when
  // they're building a 12-team slate. `slateTeams === null` means the
  // slate hasn't loaded yet — fall back to showing everything.
  const vegasSorted = React.useMemo(() => {
    // Sprint 60.102 — filter by game_id (AWAY@HOME) rather than team
    // overlap. The prior team-based filter matched any Vegas game
    // where EITHER team was in the slate — which leaked Thu-Night or
    // Mon-Night games onto a Sunday slate view. Game-id filter is
    // exact-match and gives us exactly the N games in the slate.
    const scoped = slateGameIds && slateGameIds.size > 0
      ? vegas.filter((g) => {
          const gid = g.game_id || (g.away && g.home ? `${g.away}@${g.home}` : null);
          return gid && slateGameIds.has(gid);
        })
      : vegas;
    return [...scoped].sort(
      (a, b) => Math.max(b.home_itt || 0, b.away_itt || 0)
              - Math.max(a.home_itt || 0, a.away_itt || 0),
    );
  }, [vegas, slateGameIds]);
  const defRanksSorted = React.useMemo(() => {
    const entries = Object.entries(defRanks);
    const scoped = slateTeams ? entries.filter(([t]) => slateTeams.has(t)) : entries;
    return scoped.sort(([a], [b]) => a.localeCompare(b));
  }, [defRanks, slateTeams]);
  const qbrTop15 = React.useMemo(
    () => Object.entries(qbr).sort(([, a], [, b]) => b - a).slice(0, 15),
    [qbr],
  );

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h2 className="heading text-2xl font-bold">Slate Intelligence</h2>
          <div className="overline mt-1">
            Vegas · QBR · Defense Rankings · Injuries · EPA {snap?.last_refresh && `· Last: ${new Date(snap.last_refresh).toLocaleString()}`}
          </div>
        </div>
        <button
          data-testid={TID.dataRefreshBtn}
          onClick={refresh}
          disabled={loading}
          className="inline-flex items-center gap-2 bg-[#39FF14] hover:bg-[#2ee30d] disabled:opacity-40 text-black font-bold uppercase tracking-widest text-xs py-2.5 px-5 transition-colors"
        >
          <ArrowClockwise size={14} weight="bold" className={loading ? "animate-spin" : ""} />
          {loading ? "Refreshing…" : "Refresh Data Now"}
        </button>
      </div>

      {/* Source status */}
      {snap?.sources_status && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          {Object.entries(snap.sources_status).map(([src, status]) => (
            <div key={src} className="bg-[#121212] border border-zinc-800 p-3">
              <div className="overline text-[9px]">{src.replace(/_/g, " ")}</div>
              <div className={`mono text-xs mt-1 ${status.startsWith("ok") ? "text-[#39FF14]" : "text-[#FFCC00]"}`}>
                {status}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Sprint 60.101 — Removed GameCardGrid from Slate Intel per
          user feedback. The Game Lock board lives on both Optimizer
          pages now where it actually gates the ILP pool; keeping it
          on Slate Intel too was redundant + the "Games (12/12 with
          live odds)" header spilled provider info. */}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* Weather Feed */}
        <WeatherPanel weather={weather} loading={weatherLoading} onRefresh={loadWeather} />

        {/* Defense Rankings */}
        <section className="bg-[#121212] border border-zinc-800">
          <div className="px-4 py-3 border-b border-zinc-800 flex items-center gap-2 flex-wrap">
            <TrendUp size={16} className="text-[#39FF14]" weight="fill" />
            <div className="heading font-bold">
              Defense-vs-Position Ranks{slateTeams ? ` (${defRanksSorted.length} teams)` : ""}
            </div>
            <div className="overline ml-2">(1 = best, 32 = worst — target 15+ for skill positions)</div>
            {slateTeams && (
              <div className="overline text-[#39FF14] ml-auto">· scoped to your slate</div>
            )}
          </div>
          <div>
            <table data-testid={TID.defRanksTable} className="w-full text-xs mono">
              <thead className="bg-black">
                <tr className="text-left text-zinc-300 uppercase tracking-widest text-[10px]">
                  <th className="px-3 py-2">Tm</th>
                  <th className="px-3 py-2">vs QB</th>
                  <th className="px-3 py-2">vs RB</th>
                  <th className="px-3 py-2">vs WR</th>
                  <th className="px-3 py-2">vs TE</th>
                  <th className="px-3 py-2">Pressure</th>
                  <th className="px-3 py-2">OL</th>
                </tr>
              </thead>
              <tbody>
                {defRanksSorted.map(([team, r]) => (
                  <tr key={team} className="border-t border-zinc-900">
                    <td className="px-3 py-2 font-bold">{team}</td>
                    <td className={`px-3 py-2 ${r.vs_qb >= 15 ? "text-[#39FF14]" : "text-zinc-500"}`}>{r.vs_qb ?? "—"}</td>
                    <td className={`px-3 py-2 ${r.vs_rb >= 15 ? "text-[#39FF14]" : "text-zinc-500"}`}>{r.vs_rb ?? "—"}</td>
                    <td className={`px-3 py-2 ${r.vs_wr >= 15 ? "text-[#39FF14]" : "text-zinc-500"}`}>{r.vs_wr ?? "—"}</td>
                    <td className={`px-3 py-2 ${r.vs_te >= 15 ? "text-[#39FF14]" : "text-zinc-500"}`}>{r.vs_te ?? "—"}</td>
                    <td className={`px-3 py-2 ${r.pressure_rank <= 15 ? "text-[#39FF14]" : "text-zinc-500"}`}>{r.pressure_rank ?? "—"}</td>
                    <td className={`px-3 py-2 ${r.ol_rank <= 15 ? "text-[#39FF14]" : "text-zinc-500"}`}>{r.ol_rank ?? "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
      </div>

      {/* Native P1 Ownership Model — the moat */}
      <OwnershipModelPanel />

      {/* Sprint 60.101 — Pivot Board hoisted up, SlateChalkPivots
          retired. Chalk/Pivots panel wasn't surfacing any pivots at
          the enforced projection floor; Pivot Board renders the
          chalk→pivot pairs correctly per position with the Fade/Ride
          actions, so it's the better single source. */}
      <PivotBoard perPosition={4} />

      {/* Sprint 60.106 — data panels hoisted ABOVE the AI-drafted plan
          blocks so users see them without scrolling past the long
          Attack Plan + Deep Focus prose. Order now:
          Injuries → Snap Surge → Vegas Slate → Top-15 QBR → AI blocks. */}

      {/* Slate Injuries */}
      <InjuryPanel injuries={injuries} loading={injuriesLoading} onRefresh={loadInjuries} />

      {/* Snap Surge Breakout Radar */}
      <SnapSurge />

      {/* Vegas Slate — scoped to teams in the currently-applied DK slate */}
      <section className="bg-[#121212] border border-zinc-800">
        <div className="px-4 py-3 border-b border-zinc-800 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Broadcast size={16} className="text-[#39FF14]" weight="fill" />
            <div className="heading font-bold">
              Vegas Slate ({vegasSorted.length} games)
            </div>
          </div>
          <div className="overline">Sorted by highest ITT</div>
        </div>        <div className="overflow-x-auto">
          <table data-testid={TID.vegasTable} className="w-full text-xs mono">
            <thead className="bg-black">
              <tr className="text-left text-zinc-300 uppercase tracking-widest text-[10px]">
                <th className="px-3 py-2">Matchup</th>
                <th className="px-3 py-2">Kickoff</th>
                <th className="px-3 py-2">Spread</th>
                <th className="px-3 py-2">O/U</th>
                <th className="px-3 py-2">Home ITT</th>
                <th className="px-3 py-2">Away ITT</th>
                <th className="px-3 py-2">Favorite</th>
              </tr>
            </thead>
            <tbody>
              {vegasSorted.map((g) => (
                <tr key={g.game_id || `${g.away}-${g.home}-${g.kickoff || ""}`} className="border-t border-zinc-900 hover:bg-zinc-900/40">
                  <td className="px-3 py-2 font-bold"><span className="inline-flex items-center gap-2"><TeamTag team={g.away} size={20} /> <span className="text-zinc-600">@</span> <TeamTag team={g.home} size={20} /></span></td>
                  <td className="px-3 py-2 text-zinc-500">{g.kickoff ? new Date(g.kickoff).toLocaleString([], { weekday: "short", hour: "numeric", minute: "2-digit" }) : "—"}</td>
                  <td className="px-3 py-2">{g.favorite} {g.spread > 0 ? `-${g.spread}` : g.spread}</td>
                  <td className="px-3 py-2 text-zinc-300">{g.total}</td>
                  <td className={`px-3 py-2 ${g.home_itt >= 24 ? "text-[#39FF14] font-bold" : ""}`}>{g.home_itt}</td>
                  <td className={`px-3 py-2 ${g.away_itt >= 24 ? "text-[#39FF14] font-bold" : ""}`}>{g.away_itt}</td>
                  <td className="px-3 py-2 text-[#39FF14]">{g.favorite}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>

      <div className="grid grid-cols-1 lg:grid-cols-1 gap-4">
        <section className="bg-[#121212] border border-zinc-800">
          <div className="px-4 py-3 border-b border-zinc-800 flex items-center gap-2">
            <div className="heading font-bold">Top-15 QBR (Season)</div>
          </div>
          <div>
            <table data-testid={TID.qbrTable} className="w-full text-xs mono">
              <tbody>
                {qbrTop15.map(([name, q]) => (
                  <tr key={name} className="border-t border-zinc-900">
                    <td className="px-3 py-2 capitalize">{name}</td>
                    <td className={`px-3 py-2 text-right font-bold ${q >= 90 ? "text-[#39FF14]" : q >= 70 ? "text-zinc-300" : "text-zinc-500"}`}>{q.toFixed(1)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
      </div>

      {/* Slate Attack Plan (AI) */}
      <StrategyModeBanner presets={presets} activeKey={activePreset} />
      <AttackPlanPanel
        plan={attackPlan}
        loading={attackPlanLoading}
        onGenerate={generateAttackPlan}
        presets={presets}
        activePreset={activePreset}
        customParams={customParams}
        onCustomParamChange={updateCustomParam}
        slateInfo={slateInfo}
        onPresetChange={(k) => {
          setActivePreset(k);
          try { localStorage.setItem(ACTIVE_PRESET_KEY, k); } catch { /* noop */ }
          try {
            window.dispatchEvent(new CustomEvent("wp:preset-change", {
              detail: { preset: k, source: "slate-intel" },
            }));
          } catch { /* noop */ }
        }}
      />

      {/* Deep Focus Analysis — merged from the former standalone AI Plan tab.
          The Attack Plan above is contest-posture-tuned; Deep Focus is
          lens-tuned (Leverage / Chalk / Stacks / Showdown / Cash) — two
          orthogonal reads on the same slate, now on one page. */}
      <AIAnalysis embedded slateInfo={slateInfo} />
      {/* Accuracy evidence lives next to the slate intel it grades. */}
      <OwnershipCalibrationPanel />
    </div>
  );
}

function AttackPlanPanel({ plan, loading, onGenerate, presets, activePreset, onPresetChange, customParams, onCustomParamChange, slateInfo }) {
  const slateLoaded = !!(slateInfo && (slateInfo.n_players || 0) > 0);
  const slateSummary = slateLoaded
    ? `${slateInfo.n_players} players · ${slateInfo.n_games || 0} games`
    : null;
  return (
    <section data-testid="attack-plan-panel" className="bg-[#121212] border border-zinc-800">
      <div className="px-4 py-3 border-b border-zinc-800 flex items-center justify-between flex-wrap gap-2">
        <div className="flex items-center gap-2 flex-wrap">
          <Sparkle size={16} weight="fill" className="text-[#39FF14]" />
          <div className="heading font-bold">Slate Attack Plan</div>
          <div className="overline ml-2">CLAUDE SONNET 5 · CHIEF STRATEGIST</div>
          {slateLoaded && (
            <span
              data-testid="attack-plan-slate-ready"
              className="inline-flex items-center gap-1.5 border border-[#39FF14] bg-[#39FF14]/10 px-2 py-0.5"
              title={`Your slate is loaded: ${slateSummary}`}
            >
              <span className="w-1.5 h-1.5 rounded-full bg-[#39FF14] animate-pulse" />
              <span className="mono text-[9px] tracking-widest text-[#39FF14] uppercase font-bold">
                Your Slate · {slateSummary}
              </span>
            </span>
          )}
        </div>
        <button
          data-testid="attack-plan-btn"
          onClick={onGenerate}
          disabled={loading}
          className="inline-flex items-center gap-2 bg-[#39FF14] hover:bg-[#2ee30d] text-black font-bold uppercase tracking-widest text-xs py-2 px-4 transition-colors disabled:opacity-50"
        >
          <Sparkle size={12} weight="fill" />
          {loading ? "Drafting…" : plan ? "Regenerate" : "Draft Attack Plan"}
        </button>
      </div>

      {/* Field-size picker — lets the user choose posture (Large / Mid / Small / Cash)
          WITHOUT bouncing back to the Optimizer or Kickoff Countdown tab. The
          picker is source-of-truth for THIS panel and broadcasts to the whole
          app via wp:preset-change so Optimizer/Data Pipeline stay in sync. */}
      {presets && Object.keys(presets).length > 0 && (
        <div className="px-4 py-3 border-b border-zinc-800 space-y-2" data-testid="attack-plan-preset-picker">
          <div className="overline">Field & Contest Posture</div>
          <div className="flex flex-wrap gap-1.5">
            {Object.entries(presets).map(([k, p]) => {
              const on = k === activePreset;
              return (
                <button
                  key={k}
                  data-testid={`attack-plan-preset-${k}`}
                  type="button"
                  onClick={() => onPresetChange(k)}
                  disabled={loading}
                  className={`px-2.5 py-1.5 text-[10px] mono uppercase tracking-widest border transition-colors ${
                    on
                      ? "bg-[#39FF14] text-black border-[#39FF14] font-bold"
                      : "bg-transparent text-zinc-300 border-zinc-800 hover:border-zinc-600 hover:text-white"
                  }`}
                >
                  {p.label}
                </button>
              );
            })}
          </div>
          {presets[activePreset]?.description && (
            <div className="text-[11px] text-zinc-500 leading-relaxed">
              {presets[activePreset].description}
            </div>
          )}
          {/* Custom-preset parameter editor — only rendered when the user
              has selected the CUSTOM posture. Otherwise the built-in preset
              values are used and this section stays hidden. */}
          {activePreset === "custom" && customParams && onCustomParamChange && (
            <CustomParamsEditor params={customParams} onChange={onCustomParamChange} />
          )}
          <div className="text-[10px] mono text-zinc-600 tracking-widest uppercase">
            {plan
              ? "Change posture → hit Regenerate to re-draft plan for the new field size"
              : "Pick a field size before drafting — the plan tunes strategy to the contest"}
          </div>
        </div>
      )}

      <div className="p-4">
        {!plan ? (
          <div className="text-xs text-zinc-400 leading-relaxed">
            {slateLoaded ? (
              <>
                <span className="text-[#39FF14] font-bold">Your slate is loaded ({slateSummary}).</span>{" "}
                Pick a field-size posture above and click <span className="text-white font-bold">Draft Attack Plan</span> —
                Claude Sonnet 5 will feed your slate's Vegas lines, weather, snap surges, injuries, and defense
                ranks into a plain-English game-by-game plan (chalk to fade, leverage to attack, stack combos).
                30-90 seconds to draft.
              </>
            ) : (
              <>Feeds Vegas + weather + snap surges + defense ranks to Claude Sonnet 5 and returns a
              plain-English game-by-game attack plan. 30-90 seconds to draft. Load a slate on the{" "}
              <span className="text-[#39FF14]">Slate Selection</span> tab to enable.</>
            )}
          </div>
        ) : (
          <div data-testid="attack-plan-body" className="prose prose-invert prose-sm max-w-none whitespace-pre-wrap text-zinc-300 text-sm leading-relaxed">
            {plan.plan}
          </div>
        )}
      </div>
    </section>
  );
}


function CustomParamsEditor({ params, onChange }) {
  // Compact grid editor for the CUSTOM contest posture. Field size drives the
  // Attack Plan's strategy_mode (large/mid/small/cash); the remaining knobs
  // feed the Optimizer's cfg via the wp:custom-preset-change broadcast so
  // "Fully manual" isn't a lie any more.
  const fields = [
    { key: "field_size", label: "Field size", min: 1, max: 2_000_000, step: 100, hint: "Total contest entries" },
    { key: "num_lineups", label: "Lineups", min: 1, max: 500, step: 1, hint: "Portfolio size" },
    { key: "max_exposure_pct", label: "Max exp %", min: 5, max: 100, step: 1, hint: "Cap any player at this % of your lineups" },
    { key: "sum_own_min", label: "ΣOwn min", min: 0, max: 300, step: 1, hint: "Lower bound on lineup total ownership" },
    { key: "sum_own_max", label: "ΣOwn max", min: 0, max: 300, step: 1, hint: "Upper bound on lineup total ownership" },
    { key: "low_owned_thresh", label: "Low-own %", min: 1, max: 20, step: 1, hint: "Own% threshold that qualifies as 'low-owned'" },
    { key: "min_low_owned", label: "Min low-own", min: 0, max: 5, step: 1, hint: "Force at least N low-owned plays per lineup" },
    { key: "global_variance", label: "Variance", min: 0, max: 0.30, step: 0.01, hint: "Projection jitter used across the portfolio" },
  ];
  return (
    <div
      data-testid="attack-plan-custom-editor"
      className="mt-2 p-3 border border-[#39FF14]/30 bg-[#39FF14]/[0.03]"
    >
      <div className="flex items-center justify-between mb-2">
        <div className="overline text-[10px] text-[#39FF14]">Custom Parameters</div>
        <div className="mono text-[9px] uppercase tracking-widest text-zinc-500">
          Field {Number(params.field_size || 0).toLocaleString()} · {params.num_lineups} lineups
        </div>
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
        {fields.map((f) => (
          <label key={f.key} className="flex flex-col gap-1" title={f.hint}>
            <span className="mono text-[9px] uppercase tracking-widest text-zinc-500">{f.label}</span>
            <input
              type="number"
              min={f.min}
              max={f.max}
              step={f.step}
              value={params[f.key] ?? ""}
              onChange={(e) => {
                const raw = e.target.value;
                if (raw === "") { onChange(f.key, ""); return; }
                const n = Number(raw);
                if (!Number.isFinite(n)) return;
                const clamped = Math.max(f.min, Math.min(f.max, n));
                onChange(f.key, clamped);
              }}
              data-testid={`custom-param-${f.key}`}
              className="bg-zinc-950 border border-zinc-800 text-[11px] text-white px-2 py-1 focus:border-[#39FF14] focus:outline-none"
            />
          </label>
        ))}
      </div>
      <div className="mono text-[9px] text-zinc-600 mt-2 uppercase tracking-widest">
        Saved automatically · used on next Draft Attack Plan · broadcast to Optimizer.
      </div>
    </div>
  );
}


function InjuryPanel({ injuries, loading, onRefresh }) {
  const matches = injuries?.matches || [];
  const badgeColor = (status) => ({
    OUT: "text-[#FF3B30] border-[#FF3B30]",
    IR: "text-[#FF3B30] border-[#FF3B30]",
    PUP: "text-[#FF3B30] border-[#FF3B30]",
    DOUBTFUL: "text-[#FFCC00] border-[#FFCC00]",
    QUESTIONABLE: "text-zinc-300 border-zinc-700",
  }[status] || "text-zinc-300 border-zinc-700");

  return (
    <section data-testid="injury-panel" className="bg-[#121212] border border-zinc-800">
      <div className="px-4 py-3 border-b border-zinc-800 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <FirstAid size={16} weight="fill" className="text-[#FF3B30]" />
          <div className="heading font-bold">Slate Injuries</div>
          <div className="overline ml-2">({matches.length} on your slate)</div>
        </div>
        <button
          onClick={onRefresh}
          disabled={loading}
          className="text-[10px] mono uppercase tracking-widest border border-zinc-800 hover:border-[#FF3B30] hover:text-[#FF3B30] text-zinc-300 px-2 py-1 transition-colors disabled:opacity-40"
        >
          <ArrowClockwise size={10} weight="bold" className={loading ? "inline animate-spin mr-1" : "inline mr-1"} />
          {loading ? "Syncing…" : "Refresh"}
        </button>
      </div>
      <div className="p-4">
        {matches.length === 0 ? (
          <div className="text-xs text-zinc-500">
            {loading
              ? "Fetching latest injury feed…"
              : "No injuries currently affect players in your DK slate. During offseason / camp, the injury feed stays quiet until Week 1 game statuses post."}
          </div>
        ) : (
          <div data-testid="injury-slate-matches" className="grid grid-cols-1 md:grid-cols-2 gap-2">
            {matches.slice(0, 20).map((m) => (
              <div key={m.dk_id || `${m.player_name}-${m.team}-${m.status}`} className={`bg-black border ${badgeColor(m.status).split(" ")[1]} p-2 flex items-center justify-between gap-2 text-xs mono`}>
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2">
                    <span className="text-[10px] uppercase text-zinc-500 w-8">{m.position}</span>
                    <span className="text-white truncate">{m.player_name}</span>
                    <span className="text-zinc-500 text-[10px]">{m.team}</span>
                  </div>
                  {m.description && (
                    <div className="text-[10px] text-zinc-500 truncate mt-0.5" title={m.description}>
                      {m.description}
                    </div>
                  )}
                </div>
                <span className={`text-[10px] mono px-2 py-0.5 border ${badgeColor(m.status)} font-bold shrink-0`}>
                  {m.status}
                </span>
              </div>
            ))}
          </div>
        )}
        {injuries?.fetched_at && (
          <div className="mt-3 text-[10px] mono text-zinc-600">
            Injury feed · fetched {new Date(injuries.fetched_at).toLocaleString()}
          </div>
        )}
      </div>
    </section>
  );
}

function WeatherPanel({ weather, loading, onRefresh }) {
  const reports = weather?.reports || {};
  const games = Object.keys(reports);
  const impactColor = (imp) => ({
    HIGH: "text-[#FF3B30] border-[#FF3B30]",
    MEDIUM: "text-[#FFCC00] border-[#FFCC00]",
    LOW: "text-zinc-300 border-zinc-700",
    NONE: "text-zinc-500 border-zinc-800",
    // Sprint 60.115 — game is beyond Open-Meteo's 16-day forecast
    // horizon. Not an error — muted teal so users don't confuse it
    // with a real problem.
    PENDING: "text-[#7DD3FC] border-[#7DD3FC]/40",
  }[imp] || "text-zinc-500 border-zinc-800");

  return (
    <section data-testid="weather-panel" className="bg-[#121212] border border-zinc-800">
      <div className="px-4 py-3 border-b border-zinc-800 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <CloudRain size={16} className="text-[#39FF14]" weight="fill" />
          <div className="heading font-bold">Weather Feed</div>
          <div className="overline ml-2">({games.length} games)</div>
        </div>
        <button
          data-testid="weather-refresh-btn"
          onClick={onRefresh}
          disabled={loading}
          className="text-[10px] mono uppercase tracking-widest border border-zinc-800 hover:border-[#39FF14] hover:text-[#39FF14] text-zinc-300 px-2 py-1 transition-colors disabled:opacity-40"
        >
          <ArrowClockwise size={10} weight="bold" className={loading ? "inline animate-spin mr-1" : "inline mr-1"} />
          {loading ? "Fetching…" : "Refresh"}
        </button>
      </div>
      <div className="p-4">
        {games.length === 0 ? (
          <div className="text-xs text-zinc-500">Upload a DK slate to load weather.</div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
            {games.map((gid) => {
              const r = reports[gid];
              return (
                <div key={gid} className={`bg-black border ${impactColor(r.impact).split(" ")[1]} p-3`}>
                  <div className="flex items-center justify-between">
                    <span className="mono text-sm font-bold">{gid}</span>
                    <span className={`text-[10px] mono px-2 py-0.5 border ${impactColor(r.impact)}`}>
                      {r.impact}
                    </span>
                  </div>
                  <div className="mt-2 flex items-center gap-3 text-xs mono text-zinc-300">
                    <span className="inline-flex items-center gap-1">
                      <Thermometer size={12} /> {r.temp_f !== null ? `${r.temp_f}°` : "—"}
                    </span>
                    <span className="inline-flex items-center gap-1">
                      <Wind size={12} /> {r.wind_mph !== null ? `${r.wind_mph}mph` : "—"}
                    </span>
                    <span className="inline-flex items-center gap-1">
                      <CloudRain size={12} /> {r.precip_pct !== null ? `${r.precip_pct}%` : "—"}
                    </span>
                  </div>
                  <div className="mt-1.5 text-[10px] text-zinc-500 truncate" title={r.conditions}>
                    {r.stadium_type === "dome" ? "DOME · " : ""}{r.conditions}
                  </div>
                  {r.notes && r.impact !== "NONE" && (
                    <div className="mt-1 text-[10px] text-[#FFCC00] leading-tight">
                      {r.notes}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </section>
  );
}


function OwnershipModelPanel() {
  return (
    <section
      data-testid="native-ownership-panel"
      className="relative overflow-hidden border border-[#39FF14]/25 bg-gradient-to-br from-[#0a1a10] via-[#0b0b0b] to-[#0a0a0a]"
      style={{ boxShadow: "0 0 40px rgba(57,255,20,0.06), inset 0 1px 0 rgba(255,255,255,0.03)" }}
    >
      <div
        className="absolute inset-0 pointer-events-none opacity-50"
        style={{ background: "radial-gradient(700px 160px at 15% 0%, rgba(57,255,20,0.14), transparent 70%)" }}
      />
      <div className="relative px-5 py-5">
        <div className="flex items-center gap-3 flex-wrap">
          <div className="inline-flex items-center gap-2 px-2 py-1 border border-[#39FF14]/40 bg-black/40">
            <Cpu size={12} weight="fill" className="text-[#39FF14]" />
            <span className="mono text-[9px] uppercase tracking-[0.28em] text-[#39FF14] font-bold">Native · P1 Ownership Model</span>
          </div>
          <span className="mono text-[9px] text-zinc-500 uppercase tracking-widest">competitor CSVs strictly optional</span>
        </div>
        <h3 className="heading text-white font-black text-2xl mt-3 leading-tight">
          We project the field <span className="text-[#39FF14]">before</span> the retail sites do.
        </h3>
        <p className="text-sm text-zinc-300 max-w-3xl mt-2 leading-relaxed">
          WarPaint predicts DraftKings ownership from a native model — not a blend of
          competitor CSVs. Every player gets a fresh number every refresh, hours before the
          public sites publish theirs. That's how the ownership model can enforce a
          75–125% product-ownership barbell your competition can't see yet.
        </p>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mt-5">
          <ModelPillar
            title="Vegas ITT + Spread Chase"
            body="Any player whose team implied total ≥ 26 gets a +2% floor; heavy favorites (spread ≥ 7) get an additional chalk-chase boost. Wired to SportsGameOdds sharp-book median with zero-line filter (no more preseason-placeholder pollution)."
          />
          <ModelPillar
            title="Prop-Line Juice"
            body="Player-prop line vs. season median produces a proprietary juice score — QBs over 245.5 pass yds, RBs over 78.5 rush yds, WRs over 62.5 rec yds all get graduated own boosts. Blended with prop volume signal (more books = higher conviction)."
          />
          <ModelPillar
            title="Same-Game Correlation"
            body="QB → WR stack correlation baked directly into ownership — when a slate produces a high-total shootout, ownership propagates through the full stack topology so the field actually flows to the games you'd want to attack."
          />
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mt-3">
          <ModelPillar
            title="Sub-Minute Injury Cascade"
            body="Dual-source injury feed with sub-minute latency (vs. retail's ~30-min lag). Late-swap outs zero the ruled-out player and re-boost the beneficiary in real time — your pool updates before Twitter does."
          />
          <ModelPillar
            title="Open-Meteo Weather"
            body="Hour-precise forecast keyed by kickoff time — wind ≥ 20mph or precip ≥ 60% flips games to HIGH impact; QB/WR/TE scale down, RB/DST scale up. Dome + retractable stadiums bypass entirely."
          />
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mt-3">
          <ModelPillar
            title="Salary Sweet Spots"
            body="Tier-Gravity magnetism — $5k–$6.9k RB/WR gets a +3% bump; min-salary chalk chase floors the cheap-plug plays. Position caps (QB 25 · RB 35 · WR 28 · TE 25 · DST 20) prevent runaway chalk. Grounded in 2022–2026 DK data."
          />
          <ModelPillar
            title="Contest Scalar + ITT Curve"
            body="Ownership recalibrates per contest tier — Milly cuts chalk floors, Cash lets them soar. Smooth exponential ITT curve replaces the flat +2% threshold so top-total teams get proportionally more juice."
          />
          <ModelPillar
            title="Sunday Injury Sweep"
            body="Model recomputes at 10:30, 11:00, and 11:30 CT on Sunday — plus daily at 5 PM CT and a 6 AM CT sharp-line refresh. Your pool reflects the freshest injury news, line moves, and ownership shifts before the noon-CT lock."
          />
        </div>

        <div className="mt-5 border-t border-zinc-800 pt-3 flex items-start gap-2 text-[11px] text-zinc-300 leading-relaxed">
          <Target size={12} weight="fill" className="text-[#39FF14] flex-shrink-0 mt-0.5" />
          <div>
            <span className="text-white font-bold">Why it matters:</span> retail sites publish blended ownership hours after ours ships. That gap is where the sub-5% pivots hide — where a 150-max Milly Maker portfolio actually differentiates from 200k copies of the same chalk build.
          </div>
        </div>
      </div>
    </section>
  );
}

function ModelPillar({ title, body }) {
  return (
    <div className="bg-black/50 border border-zinc-900 hover:border-zinc-700 transition-colors px-4 py-3">
      <div className="overline text-[#39FF14] text-[9px]">{title}</div>
      <div className="text-xs text-zinc-300 leading-relaxed mt-1.5">{body}</div>
    </div>
  );
}
